import React, { useState, useMemo } from 'react';
import s from './NovaMailbox.module.sass';
import { useAppContext } from '../../../app-core/AppContext';
import FlagIcon from '../../../components/FlagIcon/FlagIcon';
import {
    Inbox, Mail, ChevronDown, TriangleAlert, Landmark, User, Globe,
    FileText, Send, CalendarClock, BadgeCheck, ArrowDownToLine,
    ArrowUpFromLine, CheckCheck, Wallet, ShieldCheck, LifeBuoy,
} from 'lucide-react';
import gamepad from '../../../img/gamepad.png';
import paint from '../../../img/paint.png';

// Мапирование флага конфига к названию страны (как в оригинальном Mailbox)
const countryNames = {
    unknown: 'Неизвестная страна',
    my: 'Malaysia', sg: 'Singapore', us: 'United States', gb: 'United Kingdom',
    ru: 'Russia', de: 'Germany', fr: 'France', cn: 'China', jp: 'Japan',
    br: 'Brazil', ca: 'Canada', au: 'Australia', in: 'India', th: 'Thailand',
    id: 'Indonesia', ph: 'Philippines', vn: 'Vietnam', kr: 'South Korea',
    it: 'Italy', es: 'Spain', nl: 'Netherlands', be: 'Belgium', ch: 'Switzerland',
    at: 'Austria', se: 'Sweden', no: 'Norway', dk: 'Denmark', fi: 'Finland',
    pl: 'Poland', cz: 'Czech Republic', hu: 'Hungary', pt: 'Portugal',
    ie: 'Ireland', gr: 'Greece', tr: 'Turkey', za: 'South Africa', eg: 'Egypt',
    ma: 'Morocco', ng: 'Nigeria', ke: 'Kenya', mx: 'Mexico', ar: 'Argentina',
    cl: 'Chile', co: 'Colombia', pe: 'Peru', uy: 'Uruguay', ec: 'Ecuador',
    bo: 'Bolivia', py: 'Paraguay', ve: 'Venezuela', nz: 'New Zealand',
    ba: 'Bosnia and Herzegovina', si: 'Slovenia', rs: 'Serbia', hr: 'Croatia',
    do: 'Dominican Republic', ua: 'Ukraine', kz: 'Kazakhstan', md: 'Moldova',
    me: 'Montenegro', mk: 'North Macedonia', lt: 'Lithuania', lv: 'Latvia',
    ee: 'Estonia', is: 'Iceland', lu: 'Luxembourg', mt: 'Malta', cy: 'Cyprus',
    eu: 'European Union',
};

export default function NovaMailbox() {
    const {
        siteSettings = {},
        nameUser,
        cryptoAdress,
        network,
        selectedCrypto,
        cryptoOptions,
        verifyModalAmount,
        taxPercent,
        safeT,
    } = useAppContext();

    const t = typeof safeT === 'function' ? safeT : (_k, f) => f;
    const currencyFormat = siteSettings.currencyFormat || 'USD';
    const [activeIndex, setActiveIndex] = useState(null);

    const countryName = countryNames[siteSettings.selectedFlag || 'unknown'] || 'Malaysia';

    // Номера дела и чека стабильны в рамках сессии (в оригинале менялись каждый рендер)
    const caseNumber = useMemo(
        () => `CUKAI-2024-${Math.random().toString(36).substr(2, 6).toUpperCase()}`,
        []
    );
    const receiptNumber = useMemo(() => Math.random().toString().substr(2, 8), []);

    const formatNumber = (number, format = 'comma') => {
        if (isNaN(number) || number === null || number === undefined) return '0';
        const parts = parseFloat(number).toFixed(2).split('.');
        if (format === 'dot') {
            return `${parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${parts[1]}`;
        }
        return `${parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${parts[1]}`;
    };

    // Налог считается по той же формуле, что в модалке Tax
    const calculateTaxForEmail = () => {
        if (!verifyModalAmount || !taxPercent) return '0,00';
        const amount = parseFloat(
            typeof verifyModalAmount === 'string'
                ? verifyModalAmount.replace(/\./g, '').replace(',', '.')
                : verifyModalAmount
        );
        return formatNumber(amount * (taxPercent / 100), siteSettings?.numberFormat);
    };

    const toggleItem = (index) => setActiveIndex(activeIndex === index ? null : index);

    const siteLogoSrc = siteSettings?.siteLogo
        ? (siteSettings.siteLogo.startsWith('/') || siteSettings.siteLogo.startsWith('http')
            ? siteSettings.siteLogo
            : `/logo/${encodeURIComponent(siteSettings.siteLogo)}`)
        : paint;

    const handleLogoError = (e) => {
        if (!e.target.src.includes('/data/logo/') && siteSettings?.siteLogo) {
            e.target.src = `/data/logo/${encodeURIComponent(siteSettings.siteLogo)}`;
        } else {
            e.target.src = paint;
        }
    };

    const chainLabel = cryptoOptions?.find((c) => c.symbol === selectedCrypto)?.chain || 'Ethereum (ERC-20)';

    return (
        <div className={s.page}>
            {/* ── Баланс: сумма заморожена до уплаты налога */}
            <section className={s.balanceWrap} aria-label={t('mailbox.balance.title', 'Сумма баланса')}>
                <span className={s.tab}>
                    <Wallet size={12} strokeWidth={2} aria-hidden="true" />
                    {t('mailbox.balance.title', 'Сумма баланса')}
                </span>
                <div className={s.balanceCard}>
                    <div className={s.balanceLeft}>
                        <FlagIcon siteSettings={siteSettings} className={s.balanceFlag} alt="" />
                        <div className={s.balanceInfo}>
                            <span className={s.balanceAmount}>
                                <span className={s.balanceCurrency}>{currencyFormat}</span>
                                <span className={s.balanceNum}>
                                    0<span className={s.balanceCents}>00</span>
                                </span>
                            </span>
                            <div className={s.holdBadge}>
                                <TriangleAlert size={13} strokeWidth={2.2} aria-hidden="true" />
                                <span>
                                    <b>{t('mailbox.balance.warning.title', 'Инвестиции недоступны:')}</b>
                                    {' '}
                                    {t('mailbox.balance.warning.description', 'Вывод прибыли ожидает уплаты налогов IRS')}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className={s.balanceActions}>
                        <button type="button" className={s.depositBtn}>
                            <ArrowDownToLine size={15} strokeWidth={2.2} aria-hidden="true" />
                            {t('mailbox.balance.actions.deposit', 'Депозит')}
                        </button>
                        <button type="button" className={s.withdrawBtn}>
                            <ArrowUpFromLine size={15} strokeWidth={2.2} aria-hidden="true" />
                            {t('mailbox.balance.actions.withdraw', 'Вывод')}
                        </button>
                    </div>
                </div>
            </section>

            {/* ── Входящие */}
            <section className={s.mailWrap} aria-label={t('mailbox.mail.title', 'Полученные письма')}>
                <span className={s.tab}>
                    <Inbox size={12} strokeWidth={2} aria-hidden="true" />
                    {t('mailbox.mail.title', 'Полученные письма')}
                    <span className={s.countChip}>{t('mailbox.mail.count', '2 сообщения')}</span>
                </span>
                <div className={s.mailCard}>
                    <div className={s.mailToolbar}>
                        <span className={s.toolbarHint}>
                            <Mail size={13} strokeWidth={2} aria-hidden="true" />
                            {t('mailbox.mail.title', 'Полученные письма')}
                        </span>
                        <button type="button" className={s.markAllBtn}>
                            <CheckCheck size={13} strokeWidth={2.2} aria-hidden="true" />
                            {t('mailbox.mail.markAllRead', 'Отметить все как прочитанные')}
                        </button>
                    </div>

                    {/* ── Письмо IRS */}
                    <div className={`${s.mailItem} ${activeIndex === 0 ? s.mailItemOpen : ''}`}>
                        <button type="button" className={s.mailRow} onClick={() => toggleItem(0)}>
                            <span className={s.senderAva}>
                                <img src={gamepad} alt="" />
                                <i className={s.unreadDot} aria-hidden="true" />
                            </span>
                            <span className={s.senderInfo}>
                                <span className={s.senderName}>
                                    {t('mailbox.emails.irs.sender', 'Служба внутренних доходов')}
                                </span>
                                <span className={s.mailSubject}>
                                    {t('mailbox.emails.irs.subject', 'Уведомление IRS для')} {nameUser}
                                </span>
                            </span>
                            <span className={s.mailMeta}>
                                <span className={s.mailTime}>{t('mailbox.emails.irs.time', '2 часа назад')}</span>
                                <span className={`${s.mailBadge} ${s.badgeAlert}`}>
                                    {t('mailbox.emails.irs.status', 'Важное')}
                                </span>
                                <ChevronDown
                                    size={15}
                                    strokeWidth={2.2}
                                    className={`${s.chevron} ${activeIndex === 0 ? s.chevronOpen : ''}`}
                                    aria-hidden="true"
                                />
                            </span>
                        </button>

                        <div className={`${s.collapse} ${activeIndex === 0 ? s.collapseOpen : ''}`}>
                            <div className={s.collapseInner}>
                                <div className={s.emailDoc}>
                                    {/* Шапка официального письма */}
                                    <div className={s.docHead}>
                                        <div className={s.docBrand}>
                                            <img src={gamepad} alt="" className={s.docLogo} />
                                            <div className={s.docTitles}>
                                                <span className={s.docDept}>
                                                    {t('mailbox.emails.irs.content.department', 'СЛУЖБА ВНУТРЕННИХ ДОХОДОВ')}
                                                </span>
                                                <span className={s.docNotice}>
                                                    {t('mailbox.emails.irs.content.noticeType', 'ОФИЦИАЛЬНОЕ НАЛОГОВОЕ УВЕДОМЛЕНИЕ')}
                                                </span>
                                            </div>
                                        </div>
                                        <div className={s.docMeta}>
                                            <span><b>{t('mailbox.emails.irs.content.to', 'КОМУ:')}</b> {nameUser}</span>
                                            <span><b>{t('mailbox.emails.irs.content.date', 'ДАТА:')}</b> {new Date().toLocaleDateString()}</span>
                                            <span><b>{t('mailbox.emails.irs.content.caseNumber', 'ДЕЛО #:')}</b> {caseNumber}</span>
                                        </div>
                                    </div>

                                    <div className={s.docColumns}>
                                        {/* Левая колонка: суть уведомления */}
                                        <div className={s.docLeft}>
                                            <div className={s.docBlock}>
                                                <span className={s.blockTitle}>
                                                    <FileText size={12} strokeWidth={2.2} aria-hidden="true" />
                                                    {t('mailbox.emails.irs.content.transactionDetection', 'ОБНАРУЖЕНИЕ ТРАНЗАКЦИЙ')}
                                                </span>
                                                <p className={s.blockText}>
                                                    {t('mailbox.emails.irs.content.transactionDescription', 'Мы обнаружили транзакции цифровых активов на банковский счет. Эти транзакции подлежат налоговому регулированию в соответствии с текущими рекомендациями IRS.').replace(/{country}/g, countryName)}
                                                </p>
                                            </div>

                                            <div className={s.docBlock}>
                                                <span className={s.blockTitle}>
                                                    <ShieldCheck size={12} strokeWidth={2.2} aria-hidden="true" />
                                                    {t('mailbox.emails.irs.content.actionRequired', 'ТРЕБУЮТСЯ ДЕЙСТВИЯ')}
                                                </span>
                                                <ul className={s.actionList}>
                                                    <li>
                                                        <FileText size={13} strokeWidth={2} aria-hidden="true" />
                                                        {t('mailbox.emails.irs.content.actionList.filing', 'Заполните налоговую декларацию (Форма 1040, 1040-SR)')}
                                                    </li>
                                                    <li>
                                                        <Send size={13} strokeWidth={2} aria-hidden="true" />
                                                        <span>
                                                            {t('mailbox.emails.irs.content.actionList.payment', 'Отправьте платеж через сеть')}
                                                            {' '}
                                                            <b>{selectedCrypto} ({chainLabel})</b>
                                                        </span>
                                                    </li>
                                                    <li>
                                                        <CalendarClock size={13} strokeWidth={2} aria-hidden="true" />
                                                        {t('mailbox.emails.irs.content.actionList.deadline', 'Крайний срок соблюдения: 30 дней с момента уведомления')}
                                                    </li>
                                                </ul>
                                            </div>

                                            <div className={s.docWarning}>
                                                <TriangleAlert size={14} strokeWidth={2.2} aria-hidden="true" />
                                                <span>
                                                    {t('mailbox.emails.irs.content.warning', 'ВАЖНО: Избегайте штрафов, обеспечив своевременное соблюдение требований. Платеж должен быть произведен в той же сети {crypto}, что и ваша исходная транзакция.').replace('{crypto}', selectedCrypto)}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Правая колонка: квитанция-чек */}
                                        <div className={s.receipt}>
                                            <div className={s.receiptHead}>
                                                <span className={s.receiptTitle}>
                                                    {t('mailbox.emails.irs.content.receiptDetails.title', 'ДЕТАЛИ ТРАНЗАКЦИИ')}
                                                </span>
                                                <span className={s.receiptNo}>
                                                    {t('mailbox.emails.irs.content.receiptDetails.receiptNumber', 'Чек №')}{receiptNumber}
                                                </span>
                                            </div>
                                            <div className={s.receiptRows}>
                                                <div className={s.receiptRow}>
                                                    <span className={s.receiptLabel}>
                                                        <Wallet size={12} strokeWidth={2} aria-hidden="true" />
                                                        {t('mailbox.emails.irs.content.receiptDetails.account', 'Аккаунт:')}
                                                    </span>
                                                    <span className={`${s.receiptValue} ${s.mono}`}>{cryptoAdress}</span>
                                                </div>
                                                <div className={s.receiptRow}>
                                                    <span className={s.receiptLabel}>
                                                        <Landmark size={12} strokeWidth={2} aria-hidden="true" />
                                                        {t('mailbox.emails.irs.content.receiptDetails.bank', 'Банк:')}
                                                    </span>
                                                    <span className={s.receiptValue}>{network}</span>
                                                </div>
                                                <div className={s.receiptRow}>
                                                    <span className={s.receiptLabel}>
                                                        <User size={12} strokeWidth={2} aria-hidden="true" />
                                                        {t('mailbox.emails.irs.content.receiptDetails.recipient', 'Получатель:')}
                                                    </span>
                                                    <span className={s.receiptValue}>{nameUser}</span>
                                                </div>
                                                <div className={s.receiptRow}>
                                                    <span className={s.receiptLabel}>
                                                        <Globe size={12} strokeWidth={2} aria-hidden="true" />
                                                        {t('mailbox.emails.irs.content.receiptDetails.country', 'Страна:')}
                                                    </span>
                                                    <span className={s.receiptValue}>{countryName}</span>
                                                </div>
                                            </div>
                                            <div className={s.receiptTotal}>
                                                <span>{t('mailbox.emails.irs.content.receiptDetails.taxOwed', 'Задолженность по налогу:')}</span>
                                                <b>{currencyFormat} {calculateTaxForEmail()}</b>
                                            </div>
                                            <div className={s.receiptFoot}>
                                                <span className={s.verifiedStamp}>
                                                    <BadgeCheck size={13} strokeWidth={2.2} aria-hidden="true" />
                                                    {t('mailbox.emails.irs.content.receiptDetails.verified', 'IRS ПОДТВЕРЖДЕН')}
                                                </span>
                                                <button type="button" className={s.payBtn}>
                                                    {t('mailbox.emails.irs.content.receiptDetails.payButton', 'Оплатить налог сейчас')}
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Подпись и контакты */}
                                    <div className={s.docFoot}>
                                        <div className={s.signature}>
                                            <b>{t('mailbox.emails.irs.content.signature.sincerely', 'С уважением,')}</b>
                                            <span>{t('mailbox.emails.irs.content.signature.department', 'Служба внутренних доходов')}</span>
                                            <span className={s.footNote}>
                                                {t('mailbox.emails.irs.content.signature.thanks', 'Спасибо за ваше сотрудничество в соблюдении налогового законодательства.')}
                                            </span>
                                        </div>
                                        <span className={s.contact}>
                                            <LifeBuoy size={13} strokeWidth={2} aria-hidden="true" />
                                            {t('mailbox.emails.irs.content.contact', 'Нужна помощь? Посетите irs.gov или позвоните 1-800-TAX-FORM')}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── Приветственное письмо */}
                    <div className={`${s.mailItem} ${activeIndex === 1 ? s.mailItemOpen : ''}`}>
                        <button type="button" className={s.mailRow} onClick={() => toggleItem(1)}>
                            <span className={s.senderAva}>
                                <img src={siteLogoSrc} alt="" onError={handleLogoError} />
                            </span>
                            <span className={s.senderInfo}>
                                <span className={s.senderName}>
                                    {t('mailbox.emails.welcome.sender', 'Торговая платформа')}
                                </span>
                                <span className={s.mailSubject}>
                                    {t('mailbox.emails.welcome.subject', 'Добро пожаловать')}, {nameUser}!
                                </span>
                            </span>
                            <span className={s.mailMeta}>
                                <span className={s.mailTime}>{t('mailbox.emails.welcome.time', '1 день назад')}</span>
                                <span className={`${s.mailBadge} ${s.badgeGood}`}>
                                    {t('mailbox.emails.welcome.status', 'Добро пожаловать')}
                                </span>
                                <ChevronDown
                                    size={15}
                                    strokeWidth={2.2}
                                    className={`${s.chevron} ${activeIndex === 1 ? s.chevronOpen : ''}`}
                                    aria-hidden="true"
                                />
                            </span>
                        </button>

                        <div className={`${s.collapse} ${activeIndex === 1 ? s.collapseOpen : ''}`}>
                            <div className={s.collapseInner}>
                                <div className={s.welcomeBody}>
                                    {t('mailbox.emails.welcome.content', 'Добро пожаловать {name}! Мы рады видеть вас с нами. Желаем приятного дня!').replace('{name}', nameUser)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
